import { Component, Injector, OnDestroy, ViewChild } from '@angular/core';

import { cloneDeep } from 'lodash';

import { KeyMessageGroupApi } from '../../../../../api/ai/model/key-message-group-api.';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { CoverageInsights } from '../../../models/coverage-insights.model';
import { AiAnalysisTypes } from '../../../../../Common/Mentions/enums/ai-analysis-types.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { CoverageAnalysisService } from '../../../services/coverage-analysis.service';
import { BehaviorSubject, catchError, map, Observable, Subject, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { AnalysisUpsellPopupComponent } from '../../../../Briefings/components/AnalysisUpsellPopup/analysis-upsell-popup.component';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { MessageService } from 'primeng/api';
import { MonitorConfigService } from '../../../../../Common/MonitorShared/services/monitor-config.service';
import { SearchStatesEnum } from '../../../enums/search-states.enum';
import { DateQueryModel } from '../../../../../Common/Forms/models/date-query.model';
import { ReportDateRangeModel } from '../../../../../Common/Reports/models/report-date-range.model';
import { ScrollPanelComponent } from '../../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { MentionsService } from '../../../../Core/services/mentions.service';
import { RelativeTimePipe } from '../../../../../Common/Misc/pipes/RelativeTime/relative-time.pipe';
import { AppearCondition, AutocompleteCustomOption } from '../../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { FolderService } from '../../../../Core/services/folder.service';
import { Folder } from '../../../models/folder.model';
import { MentionCardClickedEvent } from '../../../../../Common/Reports/components/ChartDrillDownDialog/chart-drill-down-dialog.component';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import {
    MentionDetailsTakeOverPanelComponent
} from '../../../../../Common/Mentions/components/MentionDetailsTakeOverPanel/mention-details-take-over-panel.component';
import {
    ManageMentionOrganizationDialogComponent
} from '../../../../../Common/Mentions/components/ManageMentionOrganization/manage-mention-organization-dialog.component';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { TargetingRhpContextsService } from '../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { MentionSearchService } from '../../../../Core/services/mention-search.service';
import { MADSummaryService } from '../MADSummary/mad-summary.service';
import { ChartStylesModel } from '../../../../../Common/Charts/models/chart-styles.model';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { DateValueXYChart } from '../../../../../Common/Charts/models/date-value-x-y-chart.model';
import { MADSummarySeriesTypesEnum } from '../../../enums/mad-series-types.enum';
import { ChartSpecificTypesEnum } from '../../../../../Common/Reports/enums/chart-specific-types.enum';
import { DateAndValueXYChartUnitEnum } from '../../../../../Common/Charts/enums/date-and-value-x-y-chart-unit.enum';
import { DateAndValueXYChartOptionsModel } from '../../../../../Common/Charts/models/date-and-value-x-y-chart-options.model';
import { ReportBlockTypeEnum } from '../../../../../Common/Reports/enums/report-block-type.enum';
import { ChartMetadataModel } from '../../../../../Common/Charts/models/chart-metadata.model';
import { ReportsChartSeriesConverterService } from '../../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportsChartDetailService } from '../../../../../Common/Reports/services/reports-chart-detail.service';
import { MonitoringAnalyticsDashboardService } from '../../../services/monitoring-analytics-dashboard.service';
import { CoverageInsightConfigModel } from '../../../../../Common/Reports/models/coverage-insight-config.model';
import { MonitoringRoutingService } from '../../../../Core/services/monitoring-routing.service';
import { ChartDrillDownTabEnum } from '../../../../../Common/Reports/enums/chart-drill-down-tab.enum';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { Am5DateValueXYChart } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5ChartSettingsService } from '@Common/Reports/services/am5-chart-settings.service';
import { Am5ChartConfigurationService } from '@Common/Reports/services/am5-chart-configuration.service';
import { Am5ChartTooltipService } from '@Common/Charts/services/am5-chart-tooltip.service';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { UserCurrency } from '@Core/Users/enums/user-currency';
import { ChartTimeIntervalsEnum } from '@Common/Reports/enums/chart-time-intervals.enum';

@Component({
    selector: 'agpr-mad-coverage-insights',
    templateUrl: './mad-coverageInsights.component.html',
    styleUrls: ['./mad-coverageInsights.component.scss'],
    providers: [Am5ChartTooltipService],
    standalone: false
})
export class MADCoverageInsightsComponent implements OnDestroy {
    readonly id = 'mad_coverage_insights';
    readonly buttonTypes = ButtonTypesEnum;
    readonly aiPopupWidth: number = 485;
    readonly aiPopupHeight: number = 372;
    readonly warningMentionCount: number = 400;
    readonly errorMentionCount: number = 10000;
    readonly messageSeverityEnum = messageSeverityEnum;

    @ViewChild('mentionScrollPanel') mentionScrollPanel: ScrollPanelComponent;
    @ViewChild('keyMessageScrollPanel') keyMessageScrollPanel: ScrollPanelComponent;

    keyMessageGroups: KeyMessageGroupApi[] = [];
    hasCoverageInsightsEnabled: boolean = false;
    loading: boolean = false;
    loadingMentions: boolean = false;
    findingKeyMessages: boolean = false;
    overlayContainer: Element;
    dateQuery: DateQueryModel;
    mentionCount: number = 0;
    selectedMentionCount: number = 0;
    mentions: Mention[] = [];
    relativeTimePipe: RelativeTimePipe;
    selectedFolder: Folder = null;
    folderList$: Observable<Folder[]>;
    saveInProgress: boolean = false;
    analysisContext: string = '';
    currencyCode: UserCurrency;

    folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Monitoring.folderPicker.accountOption',
            action: (searchString: string) => {},
            appearCondition: AppearCondition.always,
            clearOnSelect: true
        },
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder, undefined, true).pipe(
                    take(1),
                    switchMap(id => this.folderService.all$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    ))
                ).subscribe(newFolder => {
                    this.selectedFolder = newFolder;
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    protected subscriptions: Subscription[] = [];
    private destroy$ = new Subject<void>();

    constructor(
        private injector: Injector,
        private monitorConfigService: MonitorConfigService,
        private folderService: FolderService,
        private coreDialogService: CoreDialogService,
        private coverageAnalysisService: CoverageAnalysisService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private mentionsService: MentionsService,
        private targetingRhpContextsService: TargetingRhpContextsService,
        private mentionSearchService: MentionSearchService,
        private madSummaryService: MADSummaryService,
        private reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        private reportsChartDetailService: ReportsChartDetailService,
        private routingService: MonitoringRoutingService,
        private am5ChartConfigurationService: Am5ChartConfigurationService,
        private am5ChartSettingsService: Am5ChartSettingsService,
        private am5ChartTooltipService: Am5ChartTooltipService,
        private currentUserStateService: CurrentUserStateService
    ) {
        this.overlayContainer = document.getElementById('main-content');
        this.relativeTimePipe = new RelativeTimePipe(this.localizationService);
        this.folderList$ = this.folderService.all$;
        this.setSubscriptions();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.destroy$.next();
        this.destroy$.complete();
    }

    analyzeCoverage(event): void {
        if (this.hasCoverageInsightsEnabled) {
            const coverageInsights = new CoverageInsights();
            coverageInsights.aiAnalysisType = AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT;
            coverageInsights.focus = this.analysisContext;
            this.findingKeyMessages = true;
            this.coverageAnalysisService.getKeyMessages(
                coverageInsights,
                this.mentionSearchService.getMentionSearchCriteriaPayload()
            ).pipe(
                takeUntil(this.destroy$),
                catchError(e => {
                    this.findingKeyMessages = false;
                    // eslint-disable-next-line max-len
                    if (e?.error?.details?.length && e?.error?.details?.[0].invalidValue === 'Invalid request, the number of mentions that allow ai process is 0.' ) {
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary : this.localizationService.transform('Reports.coverageInsight.error.noMentionAllowed.summary'),
                            detail: this.localizationService.transform('Reports.coverageInsight.error.noMentionAllowed.detail')
                        });
                    } else {
                        this.errorMessage();
                    }

                    throw e;
                }))
                .subscribe(keyMessages => {
                    this.keyMessageGroups = keyMessages;
                    this.resetScrollPanel();
                    this.findingKeyMessages = false;

                    if (this.keyMessageGroups?.length) {
                    // by default, selected the first key narrative
                        this.selectKeyMessage(this.keyMessageGroups[0]);
                    }
                });
        } else {
            const mousePositionX = (event as PointerEvent)?.clientX || 0;
            const mousePositionY = (event as PointerEvent)?.clientY || 0;
            const ioModel = this.coreDialogService.getIoConfigBasedOnMousePosition(mousePositionX, mousePositionY, this.aiPopupWidth, this.aiPopupHeight);
            ioModel.inputs.overlayContainer = this.overlayContainer;
            ioModel.inputs.message = 'Reports.coverageInsight.upSell.message';
            ioModel.inputs.bullets = [
                'Reports.coverageInsight.upSell.bullet1',
                'Reports.coverageInsight.upSell.bullet2',
                'Reports.coverageInsight.upSell.bullet3'
            ];
            this.coreDialogService.open(AnalysisUpsellPopupComponent, ioModel, this.injector);
        }
    }

    private errorMessage() {
        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary : this.localizationService.transform('Reports.coverageInsight.error.genericSummary'),
            detail: this.localizationService.transform('Reports.coverageInsight.error.genericDetail')
        });
    }

    /**
             * Sets subscriptions, pushing to array for easy cleanup later
             */
    private setSubscriptions(): void {
        this.subscriptions.push(
            this.routingService.getCurrentRoute().subscribe(route => {
                this.findingKeyMessages = false;
                this.destroy$.next();
            }),
            this.monitorConfigService.getHasCoverageInsightsEnabled().subscribe(x => {
                this.hasCoverageInsightsEnabled = x;
            }),
            this.mentionSearchService.getDateQuery().subscribe(dateQuery => {
                this.dateQuery = dateQuery;
            }),
            this.mentionSearchService.getSearchState().subscribe(searchState => {
                this.loading = searchState.type === SearchStatesEnum.loading;
                if (searchState.type === SearchStatesEnum.success) {
                    this.mentions = [];
                    this.keyMessageGroups = [];
                }
            }),

            this.mentionSearchService.getItemCount().subscribe(count => {
                this.mentionCount = count;
            }),
            this.currentUserStateService.getUserCurrency().subscribe(x => this.currencyCode = x)
        );
    }

    selectKeyMessage(keyMessageGroup: KeyMessageGroupApi): void {
        if (keyMessageGroup.selected) {
            return;
        }

        this.keyMessageGroups.forEach(x => x.selected = false);
        keyMessageGroup.selected = true;
        this.loadingMentions = true;
        this.selectedMentionCount = 0;
        const ids = keyMessageGroup.examples.filter(x => !!x);
        if (!ids?.length) {
            this.loadingMentions = false;
            this.mentions = [];
            return;
        }

        let mentionRequest;
        if (this.mentionSearchService.getMentionSearchCriteriaPayload().isAccountSearch) {
            mentionRequest = this.mentionsService.getMentionByIds(ids).pipe(map(res => {
                const mentions = [];
                keyMessageGroup.examples.forEach(id => {
                    if (res[id]) {
                        mentions.push(res[id]);
                    }
                });

                return mentions;
            }));
        } else {
            mentionRequest = this.mentionsService.getMentionByMercuryIds(ids);
        }
        mentionRequest.subscribe(mentions => {
            this.mentions = [];
            requestAnimationFrame(() => {
                this.mentionScrollPanel?.scrollTo();
            });
            mentions.forEach(mentionApi => {
                if (mentionApi) {
                    const mention = new Mention(mentionApi);
                    mention.relativeDateString = this.relativeTimePipe.transform(mention.dateAvailable);
                    this.mentions.push(mention);
                }
            });
            this.loadingMentions = false;
        });
    }

    saveSelectedMentions(): void {
        this.saveInProgress = true;
        const selectedMentions = this.mentions.filter(x => x.selected);
        if (this.selectedFolder) {
            this.folderService.saveMentionsToFolder(
                selectedMentions,
                this.selectedFolder,
                false
            ).subscribe(() => {
                this.saveInProgress = false;
            });
        } else {
            this.mentionsService.saveMentionsToAccount(
                selectedMentions,
                false,
            ).subscribe(() => {
                this.saveInProgress = false;
            });
        }
    }

    toggleSelectAll(): void {
        if (this.selectedMentionCount > 0) {
            this.mentions.forEach(x => x.selected = false);
            this.selectedMentionCount = 0;
        } else {
            this.mentions.forEach(x => x.selected = true);
            this.selectedMentionCount = this.mentions.length;
        }
    }

    mentionSelectChange(): void {
        this.selectedMentionCount = this.mentions.filter(x => x.selected).length;
    }

    mentionCardClicked(event: MentionCardClickedEvent) {
        if (!event || !event.mention) {
            return;
        }

        this.mentionsService.setFocusedMention(event.mention);
        this.setRHPContextFromMention(event.mention);
        this.openMentionDetailsTakeOver(event.eventType);
    }

    private setRHPContextFromMention(mention: Mention) {
        if (mention.author?.uuid) {
            this.targetingRhpContextsService.setFullProfileContactById(mention.author.uuid);
        } else {
            this.targetingRhpContextsService.clearContact();
        }
        if (mention.publication?.uuid) {
            this.targetingRhpContextsService.setFullProfileOutletById(mention.publication.uuid);
        } else {
            this.targetingRhpContextsService.clearOutlet();
        }
    }

    private openMentionDetailsTakeOver(eventType: 'contact' | 'outlet' | 'mention') {
        const io: DialogIoModel = {
            inputs: {
                focusTab: eventType,
                fromReports: true
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionDetailsTakeOverPanelComponent, io, this.injector);
    }

    openManageMentionOrganizationDialog(mention: Mention) {
        this.mentionsService.setFocusedMention(mention);

        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                mention
            },
            outputs: {
                dialogClose: (newMention: Mention) => {
                    if (newMention) {
                        const {folderIds = [], tagIds = []} = newMention;
                        mention.folderIds = folderIds;
                        mention.tagIds = tagIds;
                    }
                }
            }
        });
    }

    openDrilldown(): void {

        this.madSummaryService.getSeriesMap().pipe(take(1)).subscribe(seriesMap => {
            const summaryTrendLineStyles = new ChartStylesModel();
            summaryTrendLineStyles.colorMap = {
                'analysisColor1': chartColors.agprBlue,
                'analysisColor2': chartColors.agprPurple,
                'analysisColor3': chartColors.agprLightBlue
            };
            const summaryTrendLineSeries = seriesMap.get(MADSummarySeriesTypesEnum.summaryTrend);
            summaryTrendLineSeries.mentions.filters.searchCriteria.query.dateQuery = this.dateQuery;
            summaryTrendLineSeries.dateRange = new ReportDateRangeModel().setFromDateQuery(this.dateQuery);
            const data = summaryTrendLineSeries.dataProvider.getValue();
            if (data.length) {
                data.forEach(x => x.valueLabel = this.localizationService.transform('Reports.formField.analysis.' + x.mappingKeys.metric));
            }
            const chartSeries = this.reportsChartSeriesConverterService.getDateValueChartSeries(
                [summaryTrendLineSeries], ReportBlockTypeEnum.COVERAGE_OVER_TIME, ChartSpecificTypesEnum.LINE, this.currencyCode
            );
            const chartDisplayElement = new ChartDisplayElementModel('', [summaryTrendLineSeries], summaryTrendLineStyles);
            chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
            chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
            const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(this.id, ReportBlockTypeEnum.COVERAGE_OVER_TIME);
            chartConfiguration.noContent.isMessageStyle = true;
            const chartSettings = this.am5ChartSettingsService.getChartSettings(
                ReportBlockTypeEnum.COVERAGE_OVER_TIME,
                chartDisplayElement
            );
            chartSeries.forEach((x, i) => {
                x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, false);
            });
            const am5Chart = new Am5DateValueXYChart(
                chartSeries,
                chartConfiguration,
                chartSettings
            );

            this.reportsChartDetailService.viewCoverageAnalysisDialog(
                summaryTrendLineSeries.getMentionSeriesUrl(),
                null,
                am5Chart,
                true,
                ChartDrillDownTabEnum.coverageInsights,
                new CoverageInsightConfigModel(cloneDeep(this.keyMessageGroups), [...this.mentions.map(item => {
                    const mention = item.clone();
                    mention.selected = item.selected;
                    return mention;
                })]),
                [summaryTrendLineSeries]
            );
        });
    }

    // This might be a bug from primeng, sometimes initial load, there is a wired scrollbar, below code resolve it
    resetScrollPanel(): void {
        requestAnimationFrame(() => {
            this.keyMessageScrollPanel?.scrollTo();
            this.mentionScrollPanel?.scrollTo();
        });
    }
}

